import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, ReplaySubject, switchMap, take, tap, catchError, EMPTY, map } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Getter for unread count
     */
    get unreadCount$(): Observable<number>
    {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetch notifications with filter: all, unread, read, new, deleted
     */
    getNotifications(filter: 'all' | 'unread' | 'read' | 'new' | 'deleted' = 'all'): Observable<any>
    {
        return this._httpClient.get<any>(`${environment.apiNest_2}/api/v1/notifications`, {
            params: { filter },
            headers: { 'Content-Type': 'application/json' }
        }).pipe(
            tap((response) => {
                console.log('API Response in Service:', response);
                const notificationsArray = response && Array.isArray(response.notifications) ? response.notifications : [];
                const formattedNotifications = notificationsArray.filter(n => !!n).map((n: any) => ({
                    id: n.id,
                    user_id: n.user_id,
                    type: n.type,
                    icon: 'heroicons_outline:bell',
                    image: n.sender?.avatar,
                    title: n.title || 'Notification',
                    description: n.body || n.message || '',
                    time: n.created_at || new Date(),
                    read: !!n.is_read,
                    is_read: !!n.is_read,
                    created_at: n.created_at,
                    link: n.link || n.data.link || '',
                    redirect_url: n.redirect_url,
                    data: n.data,
                    firebase_token: n.firebase_token,
                    internal_push_id: n.internal_push_id,
                    sender: n.sender,
                    priority: n.priority,
                    read_at: n.read_at,
                    deleted_at: n.deleted_at,
                }));

                console.log('Formatted Notifications in Service:', formattedNotifications);
                this._notifications.next(formattedNotifications);

                if (response && response.newCount !== undefined) {
                    console.log('New count in Service:', response.newCount);
                    this._unreadCount.next(response.newCount);
                }
            }),
            catchError((err: any) => {
                console.error('Error Fetching Notifications:', err);
                return EMPTY;
            })
        );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>(`${environment.apiNest_2}/api/v1/notifications`, {notification}).pipe(
                tap((newNotification) =>
                {
                    this._notifications.next([...notifications, newNotification]);
                })
            ))
        );
    }

    /**
     * Mark notifications as read
     *
     * @param ids
     */
    markAsRead(ids: string[]): Observable<any>
    {
        return this._httpClient.patch(`${environment.apiNest_2}/api/v1/notifications/mark-as-read`, ids).pipe(
            tap(() => this.getNotifications().subscribe()),
            catchError((err) => {
                console.error('Error Marking Notifications as Read:', err);
                return EMPTY;
            })
        );
    }

    /**
     * Mark notifications as unread
     *
     * @param ids
     */
    markAsUnread(ids: string[]): Observable<any>
    {
        return this._httpClient.patch(`${environment.apiNest_2}/api/v1/notifications/mark-as-unread`, ids).pipe(
            tap(() => this.getNotifications().subscribe())
        );
    }

    /**
     * Delete multiple notifications
     *
     * @param ids
     */
    deleteMany(ids: string[]): Observable<any>
    {
        return this._httpClient.request('delete', `${environment.apiNest_2}/api/v1/notifications/delete-many`, { body: ids }).pipe(
            tap(() => this.getNotifications().subscribe())
        );
    }

}
